import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { AuthUser } from '../../types';
import { dataService } from '@foodbook/core-logic';
import { authService, getCurrentUser, onAuthStateChanged, logout, isSelfHostedAuthConfigured } from '@foodbook/core-logic';
import { saveAuthState, loadAuthState, clearAuthState, hasStoredAuthState } from '@foodbook/core-logic';
import { AUTH_INVALID_EVENT, getStoredAuthToken } from '@foodbook/core-logic';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (user: AuthUser) => void;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<AuthUser>) => void;
  isAuthEnabled: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PLACEHOLDER_AUTH_IDS = new Set(['local', 'anonymous']);

function normalizeAuthUser(user: Partial<AuthUser> | null | undefined): AuthUser | null {
  if (!user) {
    return null;
  }

  const candidates = [user.uid, user.id]
    .map((value) => typeof value === 'string' ? value.trim() : '')
    .filter(Boolean);

  const stableId = candidates.find((value) => !PLACEHOLDER_AUTH_IDS.has(value));
  if (!stableId) {
    return null;
  }

  return {
    ...user,
    id: stableId,
    uid: stableId,
  } as AuthUser;
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthEnabled] = useState(isSelfHostedAuthConfigured());
  const isFallbackSessionRef = useRef(false);

  const login = useCallback((newUser: AuthUser) => {
    const normalizedUser = normalizeAuthUser(newUser);
    if (!normalizedUser) {
      console.warn('[AuthProvider] 登录态缺少有效 uid，已忽略本次登录状态写入');
      return;
    }
    setUser(normalizedUser);
    setIsAuthenticated(true);
    isFallbackSessionRef.current = false;
    // 保存到 localStorage 作为备份
    saveAuthState(normalizedUser);
  }, []);

  const logoutUser = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('退出登录失败:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      isFallbackSessionRef.current = false;
      // 清除 localStorage 中的认证状态
      clearAuthState();
      // 清除 dataService 缓存
      dataService.clearCache();
    }
  }, []);

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return null;
      return { ...prev, ...updates };
    });
  }, []);

  // 初始化：检查登录状态
  useEffect(() => {
    if (!isAuthEnabled) {
      setIsLoading(false);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let isInitialized = false;

    const initializeAuth = async () => {
      try {
        // 等待一小段时间确保 SDK 完全初始化
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 优先尝试从自托管会话恢复当前用户
        let currentUser = await getCurrentUser();
        
        // 如果第一次获取失败，等待后重试（SDK可能还在初始化）
        if (!currentUser) {
          await new Promise(resolve => setTimeout(resolve, 500));
          currentUser = await getCurrentUser();
        }
        
        if (currentUser) {
          const normalizedUser = normalizeAuthUser(currentUser);
          if (normalizedUser) {
            setUser(normalizedUser);
            setIsAuthenticated(true);
            saveAuthState(normalizedUser);
            isInitialized = true;
            console.log('[AuthProvider] 从自托管会话恢复登录状态:', normalizedUser.email || normalizedUser.id);
          }
        } else {
          // 服务端会话没有立即恢复时，使用本地备份态兜底。
          // 这能覆盖开发环境和生产环境的刷新场景，但本地状态仍受过期时间控制。
          const storedUser = hasStoredAuthState() && getStoredAuthToken() ? normalizeAuthUser(loadAuthState()) : null;
          if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
            isFallbackSessionRef.current = true;
            saveAuthState(storedUser);
            console.log('[AuthProvider] 从本地备份恢复登录状态:', storedUser.email || storedUser.id);
            console.warn('[AuthProvider] 当前使用本地备份会话，等待自托管服务恢复真实会话');
          } else if (hasStoredAuthState()) {
            clearAuthState();
          }
        }

        // 监听登录状态变化
        const unsubscribeFunc = onAuthStateChanged((authUser) => {
          if (authUser) {
            const normalizedUser = normalizeAuthUser(authUser);
            if (!normalizedUser) {
              clearAuthState();
              return;
            }
            setUser(normalizedUser);
            setIsAuthenticated(true);
            isFallbackSessionRef.current = false;
            saveAuthState(normalizedUser);
          } else {
            // 如果当前只是本地备份态，不要在 SDK 首次回调 null 时立刻把它清掉。
            if (isFallbackSessionRef.current && hasStoredAuthState() && getStoredAuthToken()) {
              return;
            }
            setUser(null);
            setIsAuthenticated(false);
            isFallbackSessionRef.current = false;
            // 清除 localStorage
            clearAuthState();
          }
        });
        
        // 确保 unsubscribe 是有效的函数
        if (typeof unsubscribeFunc === 'function') {
          unsubscribe = unsubscribeFunc;
        }
      } catch (error) {
        console.error('初始化认证失败:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const handleAuthInvalid = () => {
      setUser(null);
      setIsAuthenticated(false);
      isFallbackSessionRef.current = false;
      clearAuthState();
      dataService.setUserId('anonymous');
      dataService.clearCache();
    };

    window.addEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);

    return () => {
      window.removeEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('取消认证监听失败:', error);
        }
      }
    };
  }, [isAuthEnabled]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout: logoutUser,
    updateUser,
    isAuthEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
